import React from 'react';
import Icon from '@ant-design/icons';
import type { CustomIconComponentProps } from '@ant-design/icons/lib/components/Icon';

// Manufacturing flow chart — 17 steps, grouped to shared glyphs (cells, magnifier, box…). 24x24 grid, 1.5 stroke, currentColor.
// Stage colours: cell bank #46783a · active substance #9a6c2c · MP processing #a33a4e · packaging & release #3f5c8f

const base = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const PreparationMasterCellBankVirusSeedSvg = (
  props: React.SVGProps<SVGSVGElement>
) => (
  <svg {...props} {...base}>
    <circle cx="9" cy="9.4" r="4.6"></circle>
    <circle cx="9" cy="9.4" r="1.4"></circle>
    <circle cx="16.4" cy="14.8" r="3.9"></circle>
    <circle cx="16.4" cy="14.8" r="1.2"></circle>
    <circle cx="8.2" cy="18.2" r="2.6"></circle>
  </svg>
);
/** Preparation — Master cell bank / virus seed — #46783a */
export const PreparationMasterCellBankVirusSeedIcon = (
  props: Partial<CustomIconComponentProps>
) => <Icon component={PreparationMasterCellBankVirusSeedSvg} {...props} />;

const PreparationWorkingCellBankSvg = (
  props: React.SVGProps<SVGSVGElement>
) => (
  <svg {...props} {...base}>
    <circle cx="9" cy="9.4" r="4.6"></circle>
    <circle cx="9" cy="9.4" r="1.4"></circle>
    <circle cx="16.4" cy="14.8" r="3.9"></circle>
    <circle cx="16.4" cy="14.8" r="1.2"></circle>
    <circle cx="8.2" cy="18.2" r="2.6"></circle>
  </svg>
);
/** Preparation — Working cell bank — #46783a */
export const PreparationWorkingCellBankIcon = (
  props: Partial<CustomIconComponentProps>
) => <Icon component={PreparationWorkingCellBankSvg} {...props} />;

const QcCellBankVirusSeedSvg = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} {...base}>
    <circle cx="10.5" cy="10.5" r="7"></circle>
    <path d="M15.6 15.6 21 21"></path>
  </svg>
);
/** QC — cell bank / virus seed — #46783a */
export const QcCellBankVirusSeedIcon = (
  props: Partial<CustomIconComponentProps>
) => <Icon component={QcCellBankVirusSeedSvg} {...props} />;

const StorageCellBankVirusSeedSvg = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} {...base}>
    <path d="M12 2.7l8.1 4.4v9.8L12 21.3 3.9 16.9V7.1z"></path>
    <path d="M3.9 7.1 12 11.6l8.1-4.5M12 11.6v9.7"></path>
  </svg>
);
/** Storage — cell bank / virus seed — #46783a */
export const StorageCellBankVirusSeedIcon = (
  props: Partial<CustomIconComponentProps>
) => <Icon component={StorageCellBankVirusSeedSvg} {...props} />;

const ActiveSubstanceSvg = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} {...base}>
    <path d="M12 2.7l8.1 4.7v9.2L12 21.3 3.9 16.6V7.4z"></path>
    <circle cx="12" cy="12" r="2.4"></circle>
  </svg>
);
/** Active substance — #9a6c2c */
export const ActiveSubstanceIcon = (
  props: Partial<CustomIconComponentProps>
) => <Icon component={ActiveSubstanceSvg} {...props} />;

const QcActiveSubstanceSvg = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} {...base}>
    <circle cx="10.5" cy="10.5" r="7"></circle>
    <path d="M15.6 15.6 21 21"></path>
  </svg>
);
/** QC — active substance — #9a6c2c */
export const QcActiveSubstanceIcon = (
  props: Partial<CustomIconComponentProps>
) => <Icon component={QcActiveSubstanceSvg} {...props} />;

const ActiveSubstancePackagingSvg = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} {...base}>
    <path d="M12 2.7l8.1 4.4v9.8L12 21.3 3.9 16.9V7.1z"></path>
    <path d="M3.9 7.1 12 11.6l8.1-4.5M12 11.6v9.7"></path>
  </svg>
);
/** Active substance packaging — #9a6c2c */
export const ActiveSubstancePackagingIcon = (
  props: Partial<CustomIconComponentProps>
) => <Icon component={ActiveSubstancePackagingSvg} {...props} />;

const ActiveSubstanceStorageDistributionSvg = (
  props: React.SVGProps<SVGSVGElement>
) => (
  <svg {...props} {...base}>
    <path d="M12 2.7l8.1 4.4v9.8L12 21.3 3.9 16.9V7.1z"></path>
    <path d="M3.9 7.1 12 11.6l8.1-4.5M12 11.6v9.7"></path>
  </svg>
);
/** Active substance storage / distribution — #9a6c2c */
export const ActiveSubstanceStorageDistributionIcon = (
  props: Partial<CustomIconComponentProps>
) => <Icon component={ActiveSubstanceStorageDistributionSvg} {...props} />;

const ActiveSubstanceIntermediateSvg = (
  props: React.SVGProps<SVGSVGElement>
) => (
  <svg {...props} {...base}>
    <path d="M4.4 3.4h4.8"></path>
    <path d="M5.3 3.4v12.2a1.5 1.5 0 0 0 3 0V3.4"></path>
    <path d="M5.3 11h3"></path>
    <path d="M11.4 6.2h4.8"></path>
    <path d="M12.3 6.2v12.2a1.5 1.5 0 0 0 3 0V6.2"></path>
    <path d="M12.3 13.2h3"></path>
    <circle cx="20" cy="9" r="1.3"></circle>
    <circle cx="20.6" cy="14.4" r="1.3"></circle>
    <path d="M20.2 10.3l0.3 2.8"></path>
  </svg>
);
/** Active substance intermediate — #9a6c2c */
export const ActiveSubstanceIntermediateIcon = (
  props: Partial<CustomIconComponentProps>
) => <Icon component={ActiveSubstanceIntermediateSvg} {...props} />;

const MedicinalProductProcessingSvg = (
  props: React.SVGProps<SVGSVGElement>
) => (
  <svg {...props} {...base}>
    <path d="M11 7A7 7 0 1 0 18 14L11 14Z"></path>
    <path d="M14.5 3.5A7 7 0 0 1 21.5 10.5L14.5 10.5Z"></path>
  </svg>
);
/** Medicinal product processing — #a33a4e */
export const MedicinalProductProcessingIcon = (
  props: Partial<CustomIconComponentProps>
) => <Icon component={MedicinalProductProcessingSvg} {...props} />;

const SterilisationSvg = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} {...base}>
    <path d="M7 7.4c1.5-1 1.7-2.4 0.6-3.8M12 7.4c1.5-1 1.7-2.4 0.6-3.8M17 7.4c1.5-1 1.7-2.4 0.6-3.8"></path>
    <rect x="3.2" y="9.6" width="17.6" height="11.2" rx="1.6"></rect>
    <circle cx="10.2" cy="15.2" r="3.2"></circle>
    <path d="M16.2 12.9h2.2M16.2 17.5h2.2"></path>
  </svg>
);
/** Sterilisation — #a33a4e */
export const SterilisationIcon = (props: Partial<CustomIconComponentProps>) => (
  <Icon component={SterilisationSvg} {...props} />
);

const QcMedicinalProductSvg = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} {...base}>
    <circle cx="10.5" cy="10.5" r="7"></circle>
    <path d="M15.6 15.6 21 21"></path>
  </svg>
);
/** QC — medicinal product — #a33a4e */
export const QcMedicinalProductIcon = (
  props: Partial<CustomIconComponentProps>
) => <Icon component={QcMedicinalProductSvg} {...props} />;

const MpStorageDistributionSvg = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} {...base}>
    <path d="M12 2.7l8.1 4.4v9.8L12 21.3 3.9 16.9V7.1z"></path>
    <path d="M3.9 7.1 12 11.6l8.1-4.5M12 11.6v9.7"></path>
  </svg>
);
/** MP storage / distribution — #a33a4e */
export const MpStorageDistributionIcon = (
  props: Partial<CustomIconComponentProps>
) => <Icon component={MpStorageDistributionSvg} {...props} />;

const PrimaryPackagingSvg = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} {...base}>
    <rect x="2.4" y="5.8" width="19.2" height="12.4" rx="1.6"></rect>
    <circle cx="7.2" cy="9.9" r="1.5"></circle>
    <circle cx="12" cy="9.9" r="1.5"></circle>
    <circle cx="16.8" cy="9.9" r="1.5"></circle>
    <circle cx="7.2" cy="14.3" r="1.5"></circle>
    <circle cx="12" cy="14.3" r="1.5"></circle>
    <circle cx="16.8" cy="14.3" r="1.5"></circle>
  </svg>
);
/** Primary packaging — #3f5c8f */
export const PrimaryPackagingIcon = (
  props: Partial<CustomIconComponentProps>
) => <Icon component={PrimaryPackagingSvg} {...props} />;

const SecondaryPackagingSvg = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} {...base}>
    <rect x="1.6" y="4.6" width="14.4" height="14.8" rx="1.3"></rect>
    <path d="M4.6 15.4h6M4.6 12.2h7.8"></path>
    <path d="M16 6.8h5a1.2 1.2 0 0 1 1.2 1.2v8a1.2 1.2 0 0 1-1.2 1.2h-5"></path>
    <circle cx="18.9" cy="9.6" r="1.15"></circle>
    <circle cx="18.9" cy="14.4" r="1.15"></circle>
  </svg>
);
/** Secondary packaging — #3f5c8f */
export const SecondaryPackagingIcon = (
  props: Partial<CustomIconComponentProps>
) => <Icon component={SecondaryPackagingSvg} {...props} />;

const BatchCertificationSvg = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} {...base}>
    <path d="M4.6 3.4a1 1 0 0 1 1-1h9.6a1 1 0 0 1 1 1v10.4H4.6z"></path>
    <path d="M7.4 6.3h6M7.4 9.3h4.2"></path>
    <circle cx="16.6" cy="16.9" r="3.4"></circle>
    <path d="M15.2 16.9l1.1 1.1 1.7-1.9"></path>
    <path d="M15 19.8 14.4 22.6l2.2-1.1 2.2 1.1-0.6-2.8"></path>
  </svg>
);
/** Batch certification — #3f5c8f */
export const BatchCertificationIcon = (
  props: Partial<CustomIconComponentProps>
) => <Icon component={BatchCertificationSvg} {...props} />;

const OmclBloodProductsVaccinesSvg = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} {...base}>
    <path d="M2.9 8.4 12 3.1l9.1 5.3z"></path>
    <path d="M6.2 10.9v7.4M12 10.9v7.4M17.8 10.9v7.4"></path>
    <path d="M3.6 21h16.8M4.8 18.3h14.4"></path>
  </svg>
);
/** OMCL (blood products / vaccines) — #3f5c8f */
export const OmclBloodProductsVaccinesIcon = (
  props: Partial<CustomIconComponentProps>
) => <Icon component={OmclBloodProductsVaccinesSvg} {...props} />;

/** Name → component registry consumed by `AntIcon`, so these resolve by string
    the same way `@ant-design/icons` names do (config, schemas, sidebar menus). */
export const CUSTOM_ICONS = {
  PreparationMasterCellBankVirusSeedIcon,
  PreparationWorkingCellBankIcon,
  QcCellBankVirusSeedIcon,
  StorageCellBankVirusSeedIcon,
  ActiveSubstanceIcon,
  QcActiveSubstanceIcon,
  ActiveSubstancePackagingIcon,
  ActiveSubstanceStorageDistributionIcon,
  ActiveSubstanceIntermediateIcon,
  MedicinalProductProcessingIcon,
  SterilisationIcon,
  QcMedicinalProductIcon,
  MpStorageDistributionIcon,
  PrimaryPackagingIcon,
  SecondaryPackagingIcon,
  BatchCertificationIcon,
  OmclBloodProductsVaccinesIcon,
};
